/**
 * Licencia de la versión para consultorios (instalada en el computador de la clínica, sin conectarse al servidor de Donna).
 * Aquí está solo la lógica pura: qué trae una licencia y en qué estado queda según la fecha. La firma se comprueba en el servidor
 * (`api/src/servicios/licencia-local.ts`).
 */
import { DIAS_PRUEBA, estadoLicencia, LISTA_FUNCIONES, PLANES_INICIALES, CLAVES_PLAN, type ClavePlan, type EstadoLicencia, type Funcion } from "./planes.ts";

/** Días de prueba al instalar, antes de cargar la licencia. */
export const DIAS_PRUEBA_CONSULTORIO = DIAS_PRUEBA;

/** Lo que la versión para consultorios nunca incluye: las funciones que necesitan la IA o la nube de Donna. */
export const FUNCIONES_SIN_IA: readonly Funcion[] = ["dictado_ia", "imagenes_ia"];

export interface DatosLicenciaLocal {
  /** versión del formato */
  v: 1;
  /** código de la instalación a la que pertenece (se muestra en Configuración → Licencia) */
  instalacion: string;
  clinica: string;
  ruc: string;
  /** AAAA-MM-DD */
  emitida: string;
  /** AAAA-MM-DD: último día de uso sin gracia */
  vence: string;
  /** null = sin tope */
  usuarios: number | null;
  sedes: number | null;
  /** null = todas las de la versión para consultorios (o las del plan, si la licencia lo indica) */
  funciones: string[] | null;
  /** plan de Donna que contrató (esencial, clínica o total): trae las funciones y los límites de ese plan. Sin plan (licencias antiguas) = todas las funciones sin IA. */
  plan?: ClavePlan | null;
  /** funciones contratadas aparte del plan (por ejemplo el club en el plan Esencial) */
  extras?: string[] | null;
}

export const planDeConsultorio = (clave: string | null | undefined) => PLANES_INICIALES.find((p) => p.clave === clave) ?? null;
export const esPlanValido = (v: unknown): v is ClavePlan => typeof v === "string" && (CLAVES_PLAN as readonly string[]).includes(v);

/**
 * Funciones de la versión para consultorios. Nunca las de IA. Con plan: las del plan más las extras que la licencia agregue.
 * Sin plan: todas, o solo las que la licencia liste en `funciones` (formato de las primeras licencias).
 */
export function funcionesConsultorio(licencia: Pick<DatosLicenciaLocal, "funciones" | "plan" | "extras"> | null): Funcion[] {
  const base = LISTA_FUNCIONES.filter((f) => !FUNCIONES_SIN_IA.includes(f));
  if (!licencia) return base;
  const plan = planDeConsultorio(licencia.plan);
  if (plan) {
    const propias = new Set<string>([...plan.funciones, ...(licencia.extras ?? [])]);
    return base.filter((f) => propias.has(f));
  }
  if (!licencia.funciones) return base;
  return base.filter((f) => licencia.funciones!.includes(f));
}

/** Tope de usuarios y de sedes: lo que diga la licencia; si no dice nada y tiene plan, el del plan; si no, sin tope. */
export function limitesConsultorio(licencia: Pick<DatosLicenciaLocal, "usuarios" | "sedes" | "plan"> | null): { usuarios: number | null; sedes: number | null } {
  const plan = planDeConsultorio(licencia?.plan);
  return { usuarios: licencia?.usuarios ?? plan?.usuarios ?? null, sedes: licencia?.sedes ?? plan?.sedes ?? null };
}

const masDias = (iso: string, n: number) => new Date(Date.parse(iso + "T12:00:00Z") + n * 86400000).toISOString().slice(0, 10);

export interface EstadoConsultorio {
  estado: EstadoLicencia;
  dias: number | null;
  soloLectura: boolean;
  /** vencimiento de la licencia, o fin de la prueba si todavía no hay licencia */
  vence: string;
  tieneLicencia: boolean;
}

/**
 * Estado de la instalación: con licencia, por su fecha de vencimiento (con los mismos días de aviso y gracia de los planes);
 * sin licencia, por los días de prueba desde que se instaló.
 */
export function estadoConsultorio(licencia: Pick<DatosLicenciaLocal, "vence"> | null, inicioPrueba: string, hoy: string): EstadoConsultorio {
  if (licencia) {
    const e = estadoLicencia({ estado: "activa", vence: licencia.vence }, hoy);
    return { ...e, vence: licencia.vence, tieneLicencia: true };
  }
  const vence = masDias(inicioPrueba, DIAS_PRUEBA_CONSULTORIO);
  const e = estadoLicencia({ estado: "prueba", vence }, hoy);
  return { ...e, vence, tieneLicencia: false };
}
